import {isMimeType} from '@lexical/utils';
import {INSERT_IMAGE_COMMAND} from 'components/DSYS/editor/plugins/ImagesPlugin';
import {RichTextMediaUpload} from 'components/DSYS/editor/plugins/MediaUploadContext';
import {INSERT_VIDEO_COMMAND} from 'components/DSYS/editor/plugins/VideosPlugin';
import {$getNodeByKey, LexicalEditor, LexicalNode, NodeKey} from 'lexical';

import {$isImageNode} from './ImageNode';
import {$isVideoNode} from './VideoNode';
import {captureVideoPoster} from './videoPoster';

export const ACCEPTABLE_FILE_TYPES = [
  'image',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/webp',
  'image/png',
  'image/jpeg',
];

export const ACCEPTABLE_VIDEO_FILE_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

export type HandleFilesOptions = {
  videoUploads?: boolean;
  /** Called when a file fails to upload; its placeholder node has already been removed. */
  onUploadError?: (file: File) => void;
};

export const acceptedFileTypes = ({videoUploads}: Pick<HandleFilesOptions, 'videoUploads'>) =>
  videoUploads ? [...ACCEPTABLE_FILE_TYPES, ...ACCEPTABLE_VIDEO_FILE_TYPES] : ACCEPTABLE_FILE_TYPES;

const uploadPath = (file: File) => `${new Date().toISOString()}_${file.name}`;

/** Keys of image/video nodes still pointing at the temporary blob URL. */
const findNodeKeysWithSrc = (editor: LexicalEditor, src: string): NodeKey[] =>
  editor.getEditorState().read(() => {
    const keys: NodeKey[] = [];
    for (const [key, node] of editor.getEditorState()._nodeMap) {
      if (($isImageNode(node) || $isVideoNode(node)) && node.getSrc() === src) {
        keys.push(key);
      }
    }
    return keys;
  });

const updateNodes = (
  editor: LexicalEditor,
  keys: NodeKey[],
  update: (node: LexicalNode) => void
): Promise<void> =>
  new Promise((resolve) => {
    if (keys.length === 0) {
      resolve();
      return;
    }
    editor.update(
      () => {
        keys.forEach((key) => {
          const node = $getNodeByKey(key);
          if (node) {
            update(node);
          }
        });
      },
      {onUpdate: resolve}
    );
  });

const uploadImage = async (file: File, mediaUpload: RichTextMediaUpload, editor: LexicalEditor) => {
  const blobUrl = URL.createObjectURL(file);
  editor.dispatchCommand(INSERT_IMAGE_COMMAND, {altText: file.name, src: blobUrl});
  try {
    const asset = await mediaUpload.uploadMedia({path: uploadPath(file), file});
    const finalUrl = mediaUpload.getMediaUrl(asset);
    await updateNodes(editor, findNodeKeysWithSrc(editor, blobUrl), (node) => {
      if ($isImageNode(node)) {
        node.setSrc(finalUrl);
      }
    });
  } catch (error) {
    await updateNodes(editor, findNodeKeysWithSrc(editor, blobUrl), (node) => node.remove());
    throw error;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};

const uploadVideo = async (file: File, mediaUpload: RichTextMediaUpload, editor: LexicalEditor) => {
  // The blob URL gives an immediately playable local preview while the upload runs.
  const blobUrl = URL.createObjectURL(file);
  editor.dispatchCommand(INSERT_VIDEO_COMMAND, {altText: file.name, src: blobUrl});
  try {
    const [asset, posterAsset] = await Promise.all([
      mediaUpload.uploadMedia({path: uploadPath(file), file}),
      captureVideoPoster(file)
        .then((poster) =>
          poster ? mediaUpload.uploadMedia({path: uploadPath(poster), file: poster}) : null
        )
        // A missing thumbnail shouldn't fail the video upload.
        .catch(() => null),
    ]);
    const finalUrl = mediaUpload.getMediaUrl(asset);
    const posterUrl = posterAsset ? mediaUpload.getMediaUrl(posterAsset) : undefined;
    await updateNodes(editor, findNodeKeysWithSrc(editor, blobUrl), (node) => {
      if ($isVideoNode(node)) {
        node.setSrcAndPoster(finalUrl, posterUrl);
      }
    });
  } catch (error) {
    await updateNodes(editor, findNodeKeysWithSrc(editor, blobUrl), (node) => node.remove());
    throw error;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};

export const handleFiles = async (
  files: File[],
  mediaUpload: RichTextMediaUpload,
  editor: LexicalEditor,
  {videoUploads = false, onUploadError}: HandleFilesOptions = {}
): Promise<void> => {
  const acceptable = acceptedFileTypes({videoUploads});

  await Promise.all(
    files.map(async (file) => {
      if (!isMimeType(file, acceptable)) {
        console.error('Unsupported file type:', file.type);
        return;
      }
      try {
        if (file.type.startsWith('video/')) {
          await uploadVideo(file, mediaUpload, editor);
        } else {
          await uploadImage(file, mediaUpload, editor);
        }
      } catch (error) {
        console.error('Media upload failed:', error);
        onUploadError?.(file);
      }
    })
  );
};
